import React from 'react';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import * as validators from '../../../util/validators';
import { getPropsForCustomUserFieldInputs } from '../../../util/userHelpers';

import { Form, PrimaryButton, FieldTextInput, CustomExtendedDataField } from '../../../components';

import FieldSelectUserType from '../FieldSelectUserType';
import UserFieldDisplayName from '../UserFieldDisplayName';
import UserFieldPhoneNumber from '../UserFieldPhoneNumber';
import MarketingConsent from '../MarketingConsent/MarketingConsent';

import { ReactComponent as GoogleIcon } from '../../../assets/icons/google.svg';

import css from './ConfirmSignupForm.module.css';

const ConfirmSignupFormComponent = props => (
  <FinalForm
    {...props}
    mutators={{ ...arrayMutators }}
    initialValues={{
      // userType is always non business user at the start (matching SignupForm behavior)
      userType: 'non-business-user',
    }}
    render={formRenderProps => {
      const {
        rootClassName,
        className,
        formId,
        handleSubmit,
        inProgress,
        invalid,
        intl,
        termsAndConditions,
        authInfo,
        idp,
        preselectedUserType,
        userTypes,
        userFields,
        values,
      } = formRenderProps;

      const { userType } = values || {};

      // email
      const emailRequired = validators.required(
        intl.formatMessage({
          id: 'ConfirmSignupForm.emailRequired',
        })
      );
      const emailValid = validators.emailFormatValid(
        intl.formatMessage({
          id: 'ConfirmSignupForm.emailInvalid',
        })
      );

      // Custom user fields. Since user types are not supported here,
      // only fields with no user type id limitation are selected.
      const userFieldProps = getPropsForCustomUserFieldInputs(userFields, intl, userType);

      const noUserTypes = !userType && !(userTypes?.length > 0);
      const userTypeConfig = userTypes.find(config => config.userType === userType);
      const showDefaultUserFields = userType || noUserTypes;

      // Find the username field from custom fields
      const usernameField = userFieldProps.find(item => item.fieldConfig.key === 'username');

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;
      const submitDisabled = invalid || submitInProgress;

      // If authInfo is not available we should not show the ConfirmForm
      if (!authInfo) {
        return;
      }

      // Initial values from idp provider
      const { email, firstName, lastName } = authInfo;
      // Combine firstName and lastName for the fullName field
      const fullNameInitial = [firstName, lastName].filter(Boolean).join(' ');

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <div className={css.hiddenField}>
            <FieldSelectUserType
              name="userType"
              userTypes={userTypes}
              hasExistingUserType={!!preselectedUserType}
              intl={intl}
            />
          </div>

          {showDefaultUserFields ? (
            <div className={css.defaultUserFields}>
              <div className={css.emailAndNameRow}>
                <FieldTextInput
                  className={css.emailField}
                  type="email"
                  id={formId ? `${formId}.email` : 'email'}
                  name="email"
                  autoComplete="email"
                  label={`${intl.formatMessage({
                    id: 'ConfirmSignupForm.emailLabel',
                  })} *`}
                  placeholder={intl.formatMessage({
                    id: 'ConfirmSignupForm.emailPlaceholder',
                  })}
                  initialValue={email}
                  validate={validators.composeValidators(emailRequired, emailValid)}
                />
                <FieldTextInput
                  className={css.fullNameField}
                  type="text"
                  id={formId ? `${formId}.fullName` : 'fullName'}
                  name="fullName"
                  autoComplete="name"
                  label="Vollständiger Name *"
                  placeholder={intl.formatMessage({
                    id: 'ConfirmSignupForm.fullNamePlaceholder',
                    defaultMessage: 'z.B. Max Mustermann',
                  })}
                  initialValue={fullNameInitial}
                  validate={validators.required(
                    intl.formatMessage({
                      id: 'ConfirmSignupForm.fullNameRequired',
                      defaultMessage: 'Name ist erforderlich',
                    })
                  )}
                />
              </div>

              {usernameField && (
                <CustomExtendedDataField
                  key={usernameField.key}
                  fieldConfig={usernameField.fieldConfig}
                  name={usernameField.name}
                  formId={formId}
                  defaultRequiredMessage={usernameField.defaultRequiredMessage}
                  className={css.row}
                />
              )}

              <UserFieldDisplayName
                formName="ConfirmSignupForm"
                className={css.row}
                userTypeConfig={userTypeConfig}
                intl={intl}
              />
              <UserFieldPhoneNumber
                formName="ConfirmSignupForm"
                className={css.row}
                userTypeConfig={userTypeConfig}
                intl={intl}
              />
            </div>
          ) : null}

          {/*{showCustomUserFields ? (
            <div className={css.customFields}>
              {otherCustomFields.map(({ key, ...fieldProps }) => (
                <CustomExtendedDataField key={key} {...fieldProps} formId={formId} />
              ))}
            </div>
          ) : null}*/}

          <div className={css.bottomWrapper}>
            {termsAndConditions}
            <MarketingConsent />
            <PrimaryButton
              type="submit"
              inProgress={submitInProgress}
              disabled={submitDisabled}
              className={css.submitButton}
            >
              <GoogleIcon className={css.googleIcon} />
              <span className={css.buttonText}>Mit Google fortsetzen</span>
            </PrimaryButton>
          </div>
        </Form>
      );
    }}
  />
);

/**
 * A component that renders the confirm signup form, which is used with SSO authentication.
 *
 * @component
 * @param {Object} props
 * @param {string} props.rootClassName - The root class name that overrides the default class css.root
 * @param {string} props.className - The class that extends the root class
 * @param {string} props.formId - The form id
 * @param {boolean} props.inProgress - Whether the form is in progress
 * @param {ReactNode} props.termsAndConditions - The terms and conditions
 * @param {string} props.preselectedUserType - The preselected user type
 * @param {propTypes.userTypes} props.userTypes - The user types
 * @param {propTypes.listingFields} props.userFields - The user fields
 * @returns {JSX.Element}
 */
const ConfirmSignupForm = props => {
  const intl = useIntl();
  return <ConfirmSignupFormComponent {...props} intl={intl} />;
};

export default ConfirmSignupForm;
